import { Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Divider, Paper, Skeleton } from "@mui/material";
import { AccountBalanceOutlined, BookmarkBorder, Groups } from "@mui/icons-material";
import {
  DocumentList,
  ExpGroup,
  ExpProcessRow,
  PageHeader,
  StepActions,
  TabBofuV2,
} from "@components/ui_v2";
import { RootState, useAppDispatch } from "@store/store";
import { setBofuTabEmpresaDocument } from "@store/slices/bofuSlice";
import { validateRequiredDocumentsAreUploaded } from "@components/TabBofu/functions";
import { BofuTabDocument } from "@interfaces/response";
import { formatCurrency, isFormStatusEditable } from "@helpers/funciones";
import { PATHS_EXPEDIENTE_V2 } from "@helpers/constantes";

const PATH_SOLICITUD = PATHS_EXPEDIENTE_V2.informacionSolicitud.path;
const PATH_REFERENCIAS = PATHS_EXPEDIENTE_V2.referencias.path;
const PATH_PERSONAS = PATHS_EXPEDIENTE_V2.personas.path;
const PATH_INFO_BANCARIA = PATHS_EXPEDIENTE_V2.informacionBancaria.path;

/** Un documento cuenta como cargado cuando tiene archivo; los múltiples, con al menos uno. */
const tieneArchivo = (docItem: BofuTabDocument) =>
  docItem.multiple ? docItem.list_values.some(Boolean) : Boolean(docItem.file_value);

export const ExpEmpresa = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const application = useSelector((state: RootState) => state.register.application);
  const bofuTabs = useSelector((state: RootState) => state.bofu.bofu_tabs_obj);
  const bofuLoading = useSelector((state: RootState) => state.bofu.isLoading);

  const monto = application.approved_amount || application.loan_amount;
  const plazo = application.approved_term || application.loan_term;

  const documentos = bofuTabs?.empresa?.documents || [];
  const cargados = documentos.filter(tieneArchivo).length;

  return (
    <Box>
      <PageHeader eyebrow="Expediente digital" title="Documentos de la empresa" />

      <TabBofuV2 active="empresa" />

      {bofuLoading ? (
        <Skeleton variant="rectangular" animation="wave" sx={{ height: 220, borderRadius: "12px" }} />
      ) : (
        <Fragment>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "22px" }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              <ExpGroup num="A" title="Procesos guiados" meta="3 procesos" />

              <Paper variant="lendia_paper" sx={{ p: "18px 20px" }}>
                <ExpProcessRow
                  title="Información de la solicitud"
                  status={application.loan_application_information_status}
                  subtitle="Proporciona tu contacto, transaccionales y declaraciones regulatorias."
                  icon={<BookmarkBorder sx={{ fontSize: 18 }} />}
                  description={
                    <>
                      <strong>{formatCurrency(monto || 0)} MXN</strong> · {plazo || 0} meses
                    </>
                  }
                  label="Completar información"
                  onClick={() => navigate(PATH_SOLICITUD)}
                  disabled={!isFormStatusEditable(application.loan_application_information_status)}
                  data-testid="expediente-empresa-solicitud-button"
                />

                <Divider sx={{ my: "18px" }} />

                <ExpProcessRow
                  title="Referencias comerciales"
                  status={application.references_status}
                  subtitle="Clientes y proveedores que podamos contactar para validar tu operación."
                  icon={<Groups sx={{ fontSize: 18 }} />}
                  description={
                    <>
                      <strong>Clientes</strong> 1/3 · <strong>Proveedores</strong> 1/3
                    </>
                  }
                  label="Completar referencias"
                  onClick={() => navigate(PATH_REFERENCIAS)}
                  disabled={!isFormStatusEditable(application.references_status)}
                  data-testid="expediente-empresa-referencias-button"
                />

                <Divider sx={{ my: "18px" }} />

                {/* Sin dato al margen: la cuenta capturada no está en el store de la solicitud. */}
                <ExpProcessRow
                  title="Información bancaria"
                  status={application.bank_information_status}
                  subtitle="Cuenta de la empresa donde depositaremos el crédito y desde donde se domiciliarán los pagos."
                  icon={<AccountBalanceOutlined sx={{ fontSize: 18 }} />}
                  label="Completar información bancaria"
                  onClick={() => navigate(PATH_INFO_BANCARIA)}
                  disabled={!isFormStatusEditable(application.bank_information_status)}
                  data-testid="expediente-empresa-bancaria-button"
                />
              </Paper>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              <ExpGroup
                num="B"
                title="Documentos fiscales y bancarios"
                meta={`${cargados} de ${documentos.length} cargados`}
              />
              <Paper variant="lendia_paper" sx={{ p: "20px 22px" }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  <DocumentList
                    documents={documentos}
                    onChange={({ document, newValue }) =>
                      dispatch(
                        setBofuTabEmpresaDocument({
                          document,
                          newValue,
                          valueAction: undefined,
                          valueIndex: undefined,
                        }),
                      )
                    }
                    onChangeMultiple={({ document, newValue, valueAction, valueIndex }) =>
                      dispatch(setBofuTabEmpresaDocument({ document, newValue, valueAction, valueIndex }))
                    }
                  />
                </Box>
              </Paper>
            </Box>
          </Box>

          {/*
           * TODO: `validateRequiredDocumentsAreUploaded` (compartida con el expediente legacy) solo
           * mira `file_value`, así que un documento `multiple` obligatorio no bloquea el continuar.
           * TODO: el legacy usa `ButtonContinue`, que además valida el `tab_status` de la sección y
           * dispara `postValidateNextStage` en el último tab; aquí se navega directo a personas.
           */}
          <StepActions
            onNext={() => navigate(PATH_PERSONAS)}
            nextLabel="Continuar a personas relacionadas"
            nextDisabled={bofuLoading || !validateRequiredDocumentsAreUploaded(documentos)}
            nextTestId="expediente-empresa-continue-button"
          />
        </Fragment>
      )}
    </Box>
  );
};
